import React, { useState, useRef, useEffect } from "react";
import ReactTooltip from "react-tooltip";

export const EllipsisTooltipText = ({
    text,
    className,
    asLabel = false,
    asHeading = false,
    isInfoField = false,
    required = false,
    isAnchorTag = false,
    href = "",
    tooltipPlace = "top",
    // Opt-in: reuse an already-mounted <ReactTooltip> with this id instead of mounting a private one.
    // Pass it when rendering these in a list (table headers) -- each extra instance makes
    // ReactTooltip.rebuild() and every DOM mutation more expensive app-wide. Omit for unchanged behavior.
    tooltipId = null,
    tooltipClassName = ""
}) => {
    const id = text
        ?.toString()
        ?.toLowerCase()
        ?.replace(/[^a-zA-Z0-9_]/g, "_");

    const [showTooltip, setShowTooltip] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        const textElement = textRef.current;
        const measure = () => {
            if (!textElement) return;
            setShowTooltip(
                textElement.scrollWidth > textElement.clientWidth || (textElement.scrollWidth === 0 && textElement.clientWidth === 0)
            );
        };
        measure();
        // Available width can change without `text` changing, so re-measure on resize.
        if (!textElement || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(textElement);
        return () => observer.disconnect();
    }, [text]);
    useEffect(() => {
        if (tooltipId && showTooltip) ReactTooltip.rebuild();
    }, [tooltipId, showTooltip]);

    // react-tooltip reads these off the target and they win over the instance's own
    // props, so a shared instance renders exactly what the private one did.
    const dataFor = tooltipId || id;
    const sharedTooltipAttrs = tooltipId
        ? {
              "data-place": tooltipPlace,
              "data-effect": "solid",
              "data-delay-show": 500,
              "data-multiline": true,
              "data-class": "rc-tooltip-custom-class",
              "data-background-color": "#007bff"
          }
        : null;

    return (
        <>
            {asLabel ? (
                <label className={className} ref={textRef} data-for={dataFor} data-tip={showTooltip ? text : null} {...sharedTooltipAttrs}>
                    {text}
                    {required && "*"}
                    {!tooltipId && showTooltip && (
                        <ReactTooltip
                            id={id}
                            className="rc-tooltip-custom-class"
                            backgroundColor="#007bff"
                            place="top"
                            effect="solid"
                            delayShow={500}
                        />
                    )}
                </label>
            ) : asHeading ? (
                <h3 className={className} ref={textRef} data-for={dataFor} data-tip={showTooltip ? text : null} {...sharedTooltipAttrs}>
                    {text}
                    {!tooltipId && showTooltip && (
                        <ReactTooltip
                            id={id}
                            className="rc-tooltip-custom-class"
                            backgroundColor="#007bff"
                            place="top"
                            effect="solid"
                            delayShow={500}
                        />
                    )}
                </h3>
            ) : isInfoField ? (
                <p className={className} ref={textRef} data-for={dataFor} data-tip={showTooltip ? text : null} {...sharedTooltipAttrs}>
                    {text}
                    {!tooltipId && showTooltip && (
                        <ReactTooltip
                            id={id}
                            className="rc-tooltip-custom-class"
                            backgroundColor="#007bff"
                            place="top"
                            effect="solid"
                            delayShow={500}
                        />
                    )}
                </p>
            ) : isAnchorTag ? (
                <a className={className} ref={textRef} data-for={dataFor} data-tip={showTooltip ? text : null} {...sharedTooltipAttrs} href={href}>
                    {text}
                    {!tooltipId && showTooltip && (
                        <ReactTooltip
                            id={id}
                            className="rc-tooltip-custom-class"
                            backgroundColor="#007bff"
                            place="top"
                            effect="solid"
                            delayShow={500}
                        />
                    )}
                </a>
            ) : (
                <span className={className} ref={textRef} data-for={dataFor} data-tip={showTooltip ? text : null} {...sharedTooltipAttrs}>
                    {text}
                    {!tooltipId && showTooltip && (
                        <ReactTooltip
                            id={id}
                            className={`rc-tooltip-custom-class ${tooltipClassName}`.trim()}
                            backgroundColor="#007bff"
                            place={tooltipPlace}
                            effect="solid"
                            delayShow={500}
                            multiline={true}
                        />
                    )}
                </span>
            )}
        </>
    );
};
